/**
 * Per-customer email rate limit — default vs custom cap.
 *
 * The per-recipient daily cap is a platform setting, but individual customers
 * legitimately want more (a busy partner tracking many orders) or fewer (one
 * that asked to be left alone). This is where that exception is set.
 *
 * A customer not listed here uses the platform default. Removing a row clears
 * the override rather than storing a value that happens to match the default,
 * so "uses the default" and "has no row" stay the same state.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Mail, Search, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { coreAPI, customersAPI } from '../../../lib/api';
import type { CustomerEmailLimit } from '../../../lib/api';

interface CustomerResult { company: number; company_name: string }

interface CustomerEmailLimitSectionProps {
  /** Platform-level cap — what every unlisted customer gets. */
  platformRateLimit: number;
}

export function CustomerEmailLimitSection({
  platformRateLimit,
}: CustomerEmailLimitSectionProps) {
  const [rows, setRows] = useState<CustomerEmailLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<CustomerResult[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await coreAPI.getCustomerEmailLimits();
      setRows(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Could not load customer email caps: ${message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Customer search to add a new override. Debounced so typing doesn't
  // hammer the directory endpoint.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const res = await customersAPI.getProfiles({ search: q, page_size: 8 });
        if (live) setResults((res.data.results || []) as unknown as CustomerResult[]);
      } catch { if (live) setResults([]); }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [search]);

  const save = async (companyId: number, cap: number | null, companyName: string) => {
    setBusyId(companyId);
    try {
      const res = await coreAPI.updateCustomerEmailLimit(companyId, cap);
      setRows((prev) => {
        const without = prev.filter((r) => r.id !== companyId);
        return cap === null
          ? without
          : [...without, res.data].sort((a, b) => a.company_name.localeCompare(b.company_name));
      });
      toast.success(cap === null
        ? `${companyName} back to the default cap`
        : `${companyName} capped at ${cap}/day`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Could not save: ${message}`);
      void load();   // never leave a cap on screen that didn't persist
    } finally {
      setBusyId(null);
    }
  };

  const addOverride = (c: CustomerResult) => {
    setSearch('');
    setResults([]);
    if (rows.some((r) => r.id === c.company)) return;
    void save(c.company, platformRateLimit, c.company_name);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Per-customer email cap
        </CardTitle>
        <CardDescription>
          How many emails one recipient at a customer can receive in any rolling
          24 hours. Customers not listed here use the platform default of{' '}
          <strong>{platformRateLimit}</strong>. Transactional and security mail
          (invites, password resets) is never capped.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="space-y-2 rounded-lg border border-destructive p-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>Retry</Button>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">Customers on a custom cap</Label>
          <span className="text-xs text-muted-foreground">
            {rows.length === 0
              ? `All customers use the platform default (${platformRateLimit}/day)`
              : `${rows.length} customer${rows.length === 1 ? '' : 's'} on a custom cap · all others use ${platformRateLimit}/day`}
          </span>
        </div>

        {/* Search to add a customer */}
        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customer to set a custom cap…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
              {results.map((c) => {
                const already = rows.some((r) => r.id === c.company);
                return (
                  <button
                    key={c.company}
                    type="button"
                    onClick={() => addOverride(c)}
                    disabled={already}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-40"
                  >
                    {c.company_name}{already && ' (already has a custom cap)'}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading customer caps…
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <div
                key={row.id}
                className="grid grid-cols-[1fr_120px_36px] items-center gap-3 rounded-lg border p-2"
              >
                <span className="truncate text-sm">{row.company_name}</span>
                <Input
                  type="number"
                  min={1}
                  disabled={busyId === row.id}
                  defaultValue={row.email_rate_limit_per_day ?? ''}
                  onBlur={(e) => {
                    const next = Math.max(1, Number(e.target.value) || 1);
                    if (next === row.email_rate_limit_per_day) return;
                    void save(row.id, next, row.company_name);
                  }}
                  aria-label={`Daily cap for ${row.company_name}`}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={busyId === row.id}
                  title="Remove override — back to the platform default"
                  onClick={() => void save(row.id, null, row.company_name)}
                >
                  {busyId === row.id
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <X className="h-4 w-4" />}
                </Button>
              </div>
            ))}
            {rows.length === 0 && !error && (
              <p className="py-2 text-sm text-muted-foreground">
                No customers have a custom cap — every customer uses the
                platform default of {platformRateLimit}/day.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
